import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Candidate, CandidateStatus } from '../types/candidate.types';
import { CandidateStatusBadge } from './CandidateStatusBadge';
import { CandidateActions } from './CandidateActions';

export interface CandidateCardProps {
  candidate: Candidate;
  onEdit: (candidate: Candidate) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: CandidateStatus) => void;
  onResendInvitation: (id: string) => void;
  onCopyLink: (shareId: string) => void;
}

function CandidateCardContent({ candidate }: { candidate: Candidate }): JSX.Element {
  return (
    <CardContent>
      <div className="flex items-center gap-4 text-sm">
        {candidate.country ? (
          <Badge variant="outline" className="text-xs">
            {candidate.country}
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
        {candidate.consentGiven ? (
          <div className="flex items-center gap-1 text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span className="text-xs">Yes</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-muted-foreground">
            <XCircle className="h-4 w-4" />
            <span className="text-xs">No</span>
          </div>
        )}
        <span className="text-muted-foreground">
          {new Date(candidate.createdAt).toLocaleDateString()}
        </span>
      </div>
    </CardContent>
  );
}

export function CandidateCard({
  candidate,
  onEdit,
  onDelete,
  onStatusChange,
  onResendInvitation,
  onCopyLink,
}: CandidateCardProps): JSX.Element {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => navigate(`/candidates/${candidate.id}`)}
            className="font-semibold text-lg text-blue-600 dark:text-blue-400 hover:underline cursor-pointer text-left"
          >
            {candidate.name}
          </button>
          <p className="text-sm text-muted-foreground">{candidate.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <CandidateStatusBadge status={candidate.status} />
          <CandidateActions
            candidate={candidate}
            onEdit={onEdit}
            onDelete={onDelete}
            onStatusChange={onStatusChange}
            onResendInvitation={onResendInvitation}
            onCopyLink={onCopyLink}
          />
        </div>
      </CardHeader>
      <CandidateCardContent candidate={candidate} />
    </Card>
  );
}
